import * as React from 'react';
import ICalendarItem from '../../../calendar/Types';
import { Order, Photographer } from '../BookingsCalendarTypes';

type useBookingInfoProps = {
    bookingInfo: Partial<ICalendarItem>;
    selectedPhotographer: Photographer;
    handleChangeBookingInfo: (newBookingInfo: Partial<ICalendarItem>) => void;
    handleChangePhotographer: (newPhotographer: Photographer) => void;
} & externalProps;

type externalProps = {
    selectedOrder?: Order;
    photographers?: Photographer[];
};

const getInitialBookingInfo = (selectedOrder, photographers) => {
    if (selectedOrder === null) {
        return null;
    }

    if (photographers && photographers.length < 1) {
        return null;
    }

    let bookedEvent =
        photographers &&
        photographers[0].bookedEvents.find(
            (event, index) => event.orderId === selectedOrder.orderId
        );
    
    if (!bookedEvent) {
        return null;
    }

    return { end: bookedEvent.end, start: bookedEvent.start, userId: bookedEvent.photographerId };
};

const getSelectedPhotographer = (photographers: Photographer[], bookingInfo) => {
    if (photographers && photographers.length < 1) {
        return null;
    }

    if (!bookingInfo) {
        return null;
    }

    const photographer: Photographer =
        photographers &&
        photographers.find((photographer) => photographer.id === bookingInfo.userId);

    return photographer;
};

function useBookingInfo(
    selectedOrder,
    photographers,
    selectedProperties,
    onChangeExecutionTime
): useBookingInfoProps {
    const [bookingInfo, setBookingInfo] = React.useState<Partial<ICalendarItem> | null>(null);
    const [selectedPhotographer, setSelectedPhotographer] = React.useState<Photographer>(
        getSelectedPhotographer(photographers, bookingInfo)
    );

    React.useEffect(() => {
        if (photographers.length > 0) {
            setBookingInfo(getInitialBookingInfo(selectedOrder, photographers));
        }
    }, [photographers, selectedOrder, selectedProperties]);

    React.useEffect(() => {
        setSelectedPhotographer(getSelectedPhotographer(photographers, bookingInfo));
        if (bookingInfo) {
            let bookingStart = new Date(bookingInfo.start).getTime();
            let bookingEnd = new Date(bookingInfo.end).getTime();

            let executionTime =
                bookingStart && bookingEnd && (bookingEnd - bookingStart) / 1000 / 60;

            if (executionTime && selectedOrder.executionTime != executionTime) {
                onChangeExecutionTime(executionTime);
            }
        }
    }, [bookingInfo]);

    const handleChangeBookingInfo = React.useCallback((newBookingInfo: Partial<ICalendarItem>) => {
        setBookingInfo(newBookingInfo);
    }, []);

    const handleChangePhotographer = (newPhotographer) => {
        setSelectedPhotographer(newPhotographer);
    };

    return {
        bookingInfo,
        selectedPhotographer,
        handleChangeBookingInfo,
        handleChangePhotographer
    };
}

export default useBookingInfo;
